import { Component, OnInit, OnDestroy, ViewChild } from "@angular/core";
import { formatUtcDate } from "../../shared/date-utils";
import { Router } from "@angular/router";
import { CmdbSyncService, SyncStatus, SyncHistory, OrionTestResult, CmdbSyncConfig } from "../../services/cmdb-sync.service";
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { interval, Subscription } from "rxjs";
import { switchMap } from "rxjs/operators";
import { MatPaginator, PageEvent } from "@angular/material/paginator";
import { CommonModule } from "@angular/common";
import { MaterialModule } from "../../material.module";
import { SpinnerComponent } from "../../shared/spinner/spinner.component";
import { CmdbSyncSkeletonComponent } from "./cmdb-sync-skeleton/cmdb-sync-skeleton.component";
import { CmdbSyncSettingsSkeletonComponent } from "./cmdb-sync-settings-skeleton/cmdb-sync-settings-skeleton.component";
import { CmdbSyncHistorySkeletonComponent } from "./cmdb-sync-history-skeleton/cmdb-sync-history-skeleton.component";

@Component({
  selector: "modernization-cmdb-sync",
  templateUrl: "./cmdb-sync.component.html",
  styleUrls: ['./cmdb-sync.component.scss'],
  imports: [CommonModule, FormsModule, ReactiveFormsModule, MaterialModule, SpinnerComponent, CmdbSyncSkeletonComponent, CmdbSyncSettingsSkeletonComponent, CmdbSyncHistorySkeletonComponent]
})
export class CmdbSyncComponent implements OnInit, OnDestroy {
  status: SyncStatus | null = null;
  history: SyncHistory[] = [];
  loading = false;
  historyLoading = false;
  triggering = false;
  error: string | null = null;
  successMessage: string | null = null;

  // Pagination
  historyPage = 1;
  historyPageSize = 20;
  historyTotalCount = 0;

  @ViewChild('historyPaginator') historyPaginator?: MatPaginator;

  private statusSubscription?: Subscription;

  // Display columns for the history grid
  displayedColumns: string[] = ['startedAt', 'completedAt', 'duration', 'records', 'triggeredBy', 'actions'];

  // Tab
  selectedTabIndex = 0;

  // Orion single-app update
  orionAppId = '';
  orionTesting = false;
  orionResult: OrionTestResult | null = null;
  orionError: string | null = null;

  // Orion bulk enrichment
  orionEnrichRunning = false;
  orionEnrichStartedMessage: string | null = null;
  orionEnrichError: string | null = null;

  // Sync settings
  settingsForm!: FormGroup;
  settingsLoading = false;
  settingsSaving = false;
  settingsSaved = false;
  settingsError: string | null = null;

  constructor(private cmdbSyncService: CmdbSyncService, private fb: FormBuilder, private router: Router) {
    this.settingsForm = this.fb.group({
      isAutoSyncEnabled: [true],
      syncIntervalHours: [1, [Validators.required, Validators.min(1), Validators.max(8760)]],
      batchSize: [100, [Validators.required, Validators.min(1), Validators.max(1000)]],
      isSyncWithOrionEnabled: [true]
    });
  }

  ngOnInit(): void {
    this.loadData(true);
    this.loadSyncConfig();
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  loadData(isInitialLoad = false): void {
    if (isInitialLoad) {
      this.loading = true;
    }
    this.error = null;

    // Load status and history
    this.cmdbSyncService.getSyncStatus().subscribe({
      next: (status) => {
        this.status = status;
        this.loadHistory(isInitialLoad);
      },
      error: (err) => {
        console.error('Error loading sync status:', err);
        this.error = 'Failed to load sync status';
        if (isInitialLoad) {
          this.loading = false;
        }
      }
    });
  }

  private startPolling(): void {
    if (this.statusSubscription) {
      return; // already polling
    }

    // Composite subscription: immediate fetch + periodic polling
    const composite = new Subscription();

    const immediateSub = this.cmdbSyncService.getSyncStatus().subscribe({
      next: (status) => {
        this.status = status;
        this.error = null;
        if (!status.isSyncRunning) {
          // If sync already finished, refresh history and don't continue polling
          this.loadHistory();
        }
      },
      error: (err) => {
        console.error('Error loading sync status:', err);
        this.error = 'Failed to load sync status';
      }
    });

    composite.add(immediateSub);

    const intervalSub = interval(5000)
      .pipe(switchMap(() => this.cmdbSyncService.getSyncStatus()))
      .subscribe({
        next: (status) => {
          this.status = status;
          this.error = null;
          if (!status.isSyncRunning) {
            this.stopPolling();
            this.loadHistory();
          }
        },
        error: (err) => {
          console.error('Error loading sync status:', err);
          this.error = 'Failed to load sync status';
        }
      });

    composite.add(intervalSub);

    this.statusSubscription = composite;
  }

  private stopPolling(): void {
    this.statusSubscription?.unsubscribe();
    this.statusSubscription = undefined;
  }

  loadHistory(isInitialLoad = false): void {
    if (!isInitialLoad) {
      this.historyLoading = true;
    }

    this.cmdbSyncService.getSyncHistory(this.historyPage, this.historyPageSize).subscribe({
      next: (response) => {
        this.history = response.items;
        this.historyTotalCount = response.totalCount;
        if (isInitialLoad) {
          this.loading = false;
        } else {
          this.historyLoading = false;
        }
        // Start polling only if there is an active job (no completedAt)
        const hasActiveJob = this.history.some(h => !h.completedAt);
        if (hasActiveJob) {
          this.startPolling();
        } else {
          this.stopPolling();
        }
      },
      error: (err) => {
        console.error('Error loading sync history:', err);
        this.error = 'Failed to load sync history';
        if (isInitialLoad) {
          this.loading = false;
        } else {
          this.historyLoading = false;
        }
      }
    });
  }

  onHistoryPageChange(event: PageEvent): void {
    this.historyPage = event.pageIndex + 1;
    this.historyPageSize = event.pageSize;
    this.loadHistory();
  }

  triggerSync(): void {
    if (this.status?.isSyncRunning) {
      return;
    }

    this.triggering = true;
    this.error = null;
    this.successMessage = null;

    this.cmdbSyncService.triggerSync().subscribe({
      next: (response) => {
        this.successMessage = 'Sync triggered successfully!';
        this.triggering = false;
        // Reload status and history after a short delay
        setTimeout(() => {
          this.loadData();
          this.successMessage = null;
        }, 2000);
      },
      error: (err) => {
        console.error('Error triggering sync:', err);
        if (err.status === 409) {
          this.error = 'Sync is already in progress';
        } else {
          this.error = err.error?.message || 'Failed to trigger sync';
        }
        this.triggering = false;
      }
    });
  }

  formatDuration(record: SyncHistory): string {
    let seconds = record.durationSeconds;
    
    // Calculate duration from dates if not available
    if (seconds === null || seconds === undefined) {
      if (record.startedAt && record.completedAt) {
        const start = new Date(record.startedAt).getTime();
        const end = new Date(record.completedAt).getTime();
        seconds = Math.floor((end - start) / 1000);
      } else {
        return 'N/A';
      }
    }
    
    if (seconds < 60) {
      return `${seconds}s`;
    }
    
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  }

  formatDate(date: Date | null): string {
    if (!date) {
      return 'N/A';
    }
    return formatUtcDate(new Date(date), 'short');
  }

  getStatusBadgeClass(success: boolean): string {
    return success ? 'badge-success' : 'badge-danger';
  }

  getTotalRecords(record: SyncHistory): number {
    return record.recordsInserted + record.recordsUpdated + record.recordsSkipped + record.recordsDeleted;
  }

  updateOrionManifest(): void {
    if (!this.orionAppId.trim()) {
      this.orionError = 'Please enter an AppId (Service ID)';
      return;
    }

    this.orionTesting = true;
    this.orionResult = null;
    this.orionError = null;

    this.cmdbSyncService.updateOrionManifest(this.orionAppId.trim()).subscribe({
      next: (result) => {
        this.orionResult = result;
        this.orionTesting = false;
      },
      error: (err) => {
        console.error('Orion update error:', err);
        this.orionError = err.error?.detail || err.error?.message || 'Failed to update Orion manifest';
        this.orionTesting = false;
      }
    });
  }

  runOrionEnrichment(): void {
    this.orionEnrichRunning = true;
    this.orionEnrichStartedMessage = null;
    this.orionEnrichError = null;

    this.cmdbSyncService.runOrionEnrichment().subscribe({
      next: (result) => {
        // Enrichment now runs in the background (decoupled from this HTTP request so Azure's
        // ~230s request limit can't cancel it). Surface a "started" message and let the Sync
        // History grid + status polling reflect live progress and the final outcome.
        this.orionEnrichStartedMessage = result?.message || 'Orion enrichment started. Progress appears in Sync History.';
        this.orionEnrichRunning = false;
        // Refresh status + history so the in-progress run appears and polling kicks in.
        this.loadData();
        // Switch to Sync History tab to watch progress.
        this.selectedTabIndex = 0;
      },
      error: (err) => {
        console.error('Orion enrichment error:', err);
        if (err.status === 409) {
          this.orionEnrichError = 'A CMDB sync or Orion enrichment is already in progress. Wait for it to finish before running Orion enrichment.';
        } else {
          this.orionEnrichError = err.error?.detail || err.error?.message || 'Failed to run Orion enrichment';
        }
        this.orionEnrichRunning = false;
        // Refresh in case a partial entry was created
        this.loadHistory();
      }
    });
  }

  get orionManifestFormatted(): string {
    if (!this.orionResult?.manifestJson) return '';
    try {
      return JSON.stringify(JSON.parse(this.orionResult.manifestJson), null, 2);
    } catch {
      return this.orionResult.manifestJson;
    }
  }

  loadSyncConfig(): void {
    this.settingsLoading = true;
    this.settingsError = null;
    this.cmdbSyncService.getSyncConfig().subscribe({
      next: (config) => {
        this.settingsForm.patchValue({
          isAutoSyncEnabled: config.isAutoSyncEnabled,
          syncIntervalHours: config.syncIntervalHours,
          batchSize: config.batchSize,
          isSyncWithOrionEnabled: config.isSyncWithOrionEnabled
        });
        this.settingsLoading = false;
      },
      error: (err) => {
        console.error('Error loading sync config:', err);
        this.settingsError = 'Failed to load sync settings';
        this.settingsLoading = false;
      }
    });
  }

  saveSyncConfig(): void {
    if (this.settingsForm.invalid) return;

    this.settingsSaving = true;
    this.settingsSaved = false;
    this.settingsError = null;

    const config: CmdbSyncConfig = {
      ...this.settingsForm.value,
      updatedAtUtc: null
    };

    this.cmdbSyncService.updateSyncConfig(config).subscribe({
      next: (updated) => {
        this.settingsForm.patchValue({
          isAutoSyncEnabled: updated.isAutoSyncEnabled,
          syncIntervalHours: updated.syncIntervalHours,
          batchSize: updated.batchSize,
          isSyncWithOrionEnabled: updated.isSyncWithOrionEnabled
        });
        this.settingsSaving = false;
        this.settingsSaved = true;
        setTimeout(() => { this.settingsSaved = false; }, 3000);
      },
      error: (err) => {
        console.error('Error saving sync config:', err);
        this.settingsError = err.error?.message || 'Failed to save settings';
        this.settingsSaving = false;
      }
    });
  }

  viewLogs(syncId: number): void {
    this.router.navigate(['/admin/cmdb-sync/history', syncId]);
  }
}
